/**
 * @author      hugo@zumper.com (Hugo Pineda)
 * @copyright   Copyright (c) 2022, Zumper
 */
/** @jsx jsx */
import { type Theme, css, jsx } from '@emotion/react'
import { BREAKPOINT_MOBILE } from '@zcode-ui/themes'
import { createLazyLoadableSection } from '@zcode/lazy-loadable-section'
import { ErrorFallback, LoadingFallback } from '@zcode/nr/es/shared/components/Fallback'
import type { ErrorBoundaryProps } from 'react-error-boundary'

import { logger } from '../../logger'

const onRenderError: ErrorBoundaryProps['onError'] = (error, info) => {
  logger.error('render error for FooterContainer lazy loadable section', info, error)
}

const onLoadingError = (error: unknown) => {
  logger.error('loading error for FooterContainer lazy loadable section', error)
}

const loadFn = /* #__LOADABLE__ */ async () =>
  await import(/* webpackChunkName: "FooterContainer" */ './FooterContainer')

const message = `Sorry, we couldn't load the footer...`
const handleReload = async () => await FooterLazyLoadableSection.reload()

const fallbackStyle = ({ colors }: Theme) => css`
  min-height: 455px;
  div:last-child {
    color: ${colors.backgroundLightest};
  }
  @media (max-width: ${BREAKPOINT_MOBILE}) {
    min-height: 1490px;
  }
`

const options = {
  errorFallback: () => (
    <ErrorFallback css={fallbackStyle} message={message} onClickReload={handleReload} />
  ),

  loadingFallback: () => <LoadingFallback css={fallbackStyle} name="footer" />,
  onRenderError,
  onLoadingError,
}

export const FooterLazyLoadableSection = createLazyLoadableSection(loadFn, options)
